import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { matchesSpotPairsCoin, rowBetweenDateInputs } from "./orderFilterHelpers";

const ORDER_DESKTOP_COL_COUNT = 6;

const COIN_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btc", label: "BTC" },
  { value: "eth", label: "ETH" },
  { value: "usdt", label: "USDT" },
  { value: "bnb", label: "BNB" },
];

/** Replace with API rows; empty shows no-data state. */
/*
const CONVERT_ROWS = Array.from({ length: 8 }, (_, i) => ({
  id: i + 1,
  time: "2026-04-20 14:22:10",
  pair: "BTC → USDT",
  spend: "0.0543 BTC",
  receive: "3,245.80 USDT",
  price: "59,756.21",
  status: "Completed",
}));
*/
const CONVERT_ROWS = [];

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const ConvertOrders = () => {
  const [coin, setCoin] = useState("all");
  const [dateFrom, setDateFrom] = useState("2026-03-16");
  const [dateTo, setDateTo] = useState("2026-04-16");
  const [openDropdown, setOpenDropdown] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return CONVERT_ROWS.filter((r) => {
      if (!matchesSpotPairsCoin(r.pair.replace(" → ", "/"), coin)) return false;
      if (!rowBetweenDateInputs(`${r.time}`, dateFrom, dateTo)) return false;
      return true;
    });
  }, [coin, dateFrom, dateTo]);

  const resetFilters = () => {
    setCoin("all");
    setDateFrom("2026-03-16");
    setDateTo("2026-04-16");
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter convert history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}cvt-dd-coin`}
          label="Coin"
          value={coin}
          options={COIN_OPTIONS}
          onChange={setCoin}
          menuKey={`${pref}cvt-coin`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <div className="so-dateBlock">
          <span className="so-dateBlock__label">Time</span>
          <div className="so-dateRow">
            <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start time" inputRef={fromRef} />
            <span className="so-dateArrow" aria-hidden="true">
              →
            </span>
            <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End time" inputRef={toRef} />
            <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={6} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const StatusPill = ({ value }) => {
    if (value === "Completed") return <span className="so-status so-status--completed">{value}</span>;
    return <span className="so-badge--closed">{value}</span>;
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-convert-title">
            <h1 id="so-convert-title" className="so-page__title">
              Convert
            </h1>

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking so-tableWrap--convert">
              <table className="so-table so-table--rowLines so-table--convert">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Coin</th>
                    <th scope="col">
                      Spend
                    </th>
                    <th scope="col">
                      Receive
                    </th>
                    <th scope="col">
                      Price
                    </th>
                    <th scope="col">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map((r) => (
                        <tr key={r.id}>
                          <td className="so-num">{r.time}</td>
                          <td>{r.pair}</td>
                          <td className="so-num">
                            <span className="so-convertSpend">{r.spend}</span>
                          </td>
                          <td className="so-num">
                            <span className="so-convertReceive">{r.receive}</span>
                          </td>
                          <td className="so-num">{r.price}</td>
                          <td>
                            <StatusPill value={r.status} />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Convert</h2>
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-convert-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Convert details">
                <div className="so-mobileCard__row">
                  <span>Time</span>
                  <span className="so-num">{r.time}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Coin</span>
                  <span>{r.pair}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Spend</span>
                  <span className="so-num so-convertSpend">{r.spend}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Receive</span>
                  <span className="so-num so-convertReceive">{r.receive}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{r.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span>
                    <StatusPill value={r.status} />
                  </span>
                </div>
              </OrderMobileCardShell>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ConvertOrders;

